import React from 'react'
import { Badge, Button, Card, CardContent, CardHeader, Pagination, Select, Table, Tbody, Td, Th, Thead, Tr } from '@/shared/ui'
import RewardsLinkStatusBadge from '../RewardsLinkStatusBadge'
import { formatCurrency, formatUsdt } from '../../utils/rewardsFormat'
import type { RewardsLinkStatus } from '../../types'

export type RewardsMasterUserRow = {
  id: string
  accountId: string
  exchangeUserId: string
  status: RewardsLinkStatus
  linkedAt: string | null
  lastSyncAt: string | null
  createdAt: string
  updatedAt: string
  ownerName: string | null
  ownerEmail: string | null
  volumeUsdt: number
  commissionUsdt: number
  discountBrl: number
}

const PAGE_SIZE = 25

const STATUS_OPTIONS: Array<{ value: RewardsLinkStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'linked', label: 'Vinculado' },
  { value: 'awaiting', label: 'Aguardando' },
  { value: 'not_linked', label: 'Nao vinculado' },
  { value: 'syncing', label: 'Sincronizando' },
  { value: 'error', label: 'Erro' },
]

const SORT_OPTIONS: Array<{ value: 'linked_desc' | 'volume_desc' | 'commission_desc' | 'discount_desc'; label: string }> = [
  { value: 'linked_desc', label: 'Mais recentes' },
  { value: 'volume_desc', label: 'Maior volume' },
  { value: 'commission_desc', label: 'Maior comissao' },
  { value: 'discount_desc', label: 'Maior desconto' },
]

export default function RewardsMasterUsersTable({
  rows,
  loading,
  error,
  onReload,
  discountEnabled,
}: {
  rows: RewardsMasterUserRow[]
  loading: boolean
  error: string | null
  onReload: () => void
  discountEnabled: boolean
}) {
  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState<RewardsLinkStatus | 'all'>('all')
  const [sortBy, setSortBy] = React.useState<(typeof SORT_OPTIONS)[number]['value']>('linked_desc')
  const [page, setPage] = React.useState(1)

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    let out = rows

    if (status !== 'all') out = out.filter((r) => r.status === status)

    if (q) {
      out = out.filter((r) => {
        const ownerName = String(r.ownerName || '').toLowerCase()
        const ownerEmail = String(r.ownerEmail || '').toLowerCase()
        const accountId = String(r.accountId || '').toLowerCase()
        const uid = String(r.exchangeUserId || '').toLowerCase()
        return ownerName.includes(q) || ownerEmail.includes(q) || accountId.includes(q) || uid.includes(q)
      })
    }

    const sorted = [...out]
    sorted.sort((a, b) => {
      if (sortBy === 'volume_desc') return (b.volumeUsdt || 0) - (a.volumeUsdt || 0)
      if (sortBy === 'commission_desc') return (b.commissionUsdt || 0) - (a.commissionUsdt || 0)
      if (sortBy === 'discount_desc') return (b.discountBrl || 0) - (a.discountBrl || 0)
      return safeDateMs(b.linkedAt || b.updatedAt || b.createdAt) - safeDateMs(a.linkedAt || a.updatedAt || a.createdAt)
    })

    return sorted
  }, [rows, search, status, sortBy])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged = React.useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return filtered.slice(start, start + PAGE_SIZE)
  }, [filtered, page])

  React.useEffect(() => {
    setPage(1)
  }, [search, status, sortBy])

  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  return (
    <Card className="border-0 shadow">
      <CardHeader className="flex flex-wrap items-center justify-between gap-3 text-base font-semibold">
        <span>Usuarios vinculados</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onReload} disabled={loading}>
            Recarregar
          </Button>
          <Badge variant="soft" color="info">
            {filtered.length} registros
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-foreground">{error}</div>}

        <div className="grid gap-2 lg:grid-cols-[1.6fr,0.9fr,0.9fr]">
          <input
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, e-mail, UUID, UID..."
          />
          <Select value={status} onChange={(e) => setStatus((e.target as HTMLSelectElement).value as any)}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Select value={sortBy} onChange={(e) => setSortBy((e.target as HTMLSelectElement).value as any)}>
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>

        {loading ? (
          <div className="text-sm text-foreground/70">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-foreground/70">Sem usuarios para exibir.</div>
        ) : (
          <div className="overflow-auto rounded-md bg-muted/20">
            <Table>
              <Thead>
                <Tr>
                  <Th>Usuario</Th>
                  <Th>UID</Th>
                  <Th>Status</Th>
                  <Th>Volume (USDT)</Th>
                  <Th>Comissao (USDT)</Th>
                  <Th>Desconto (BRL)</Th>
                  <Th>Vinculo</Th>
                  <Th>Ultima sync</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paged.map((row) => {
                  const ownerLabel = row.ownerName || row.ownerEmail || row.accountId
                  const ownerSub = row.ownerName ? (row.ownerEmail || row.accountId) : row.accountId
                  return (
                    <Tr key={row.id}>
                      <Td>
                        <div className="min-w-[220px]">
                          <div className="text-sm font-medium">{ownerLabel}</div>
                          <div className="text-xs text-foreground/60">{ownerSub}</div>
                        </div>
                      </Td>
                      <Td className="font-mono text-xs">{row.exchangeUserId}</Td>
                      <Td><RewardsLinkStatusBadge status={row.status} /></Td>
                      <Td className="text-sm">{formatUsdt(row.volumeUsdt)} USDT</Td>
                      <Td className="text-sm">{formatUsdt(row.commissionUsdt)} USDT</Td>
                      <Td className="text-sm">{discountEnabled ? formatCurrency(row.discountBrl, 'BRL') : '-'}</Td>
                      <Td className="text-xs">{formatDate(row.linkedAt)}</Td>
                      <Td className="text-xs">{formatDate(row.lastSyncAt)}</Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="pt-2">
            <Pagination page={page} total={totalPages} onChange={setPage} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function formatDate(value?: string | null): string {
  if (!value) return '-'
  try {
    return new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
  } catch {
    return value
  }
}

function safeDateMs(value?: string | null): number {
  const ms = value ? Date.parse(value) : NaN
  return Number.isFinite(ms) ? ms : 0
}
